import "./styles.css";
import { useContext, useState, useEffect } from "react";
import { Context } from "../../../hooks/useAuthContext";
import EstadosECidades from "../../../data/estadosCidades";
import api from "../../../services/axiosConfig";
import { formatCpfCnpj } from "../../../utils/cpfCnpjFormatter";
import { formatPhone } from "../../../utils/telefoneFormatter";
import { toast } from "react-toastify";
import { UploadButton } from "../../../components/UploadButton/UploadButton";
import FullScreenLoader from "../../../components/FullScreenLoader/FullScreenLoader";
import ModalChavesPix from "../../../components/ModalChavesPix/ModalChavesPix";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { validarSenhaSegura } from "../../../utils/ValidatePassword";
import { RiProhibitedLine } from "react-icons/ri";

const LISTA_REDES_SOCIAIS = ["Instagram", "YouTube", "Facebook", "Tiktok", "Kwai", "Threads", "Twitter/X"];

function AtualizarDados() {
  const { userData, exibirLogs, fetchLoggedUser } = useContext(Context);
  const location = useLocation();

  const initialStateRedes = LISTA_REDES_SOCIAIS.reduce((acc, rede) => {
    acc[rede] = '';
    return acc;
  }, {});

  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [telefone, setTelefone] = useState("");
  const [estado, setEstado] = useState("");
  const [cidade, setCidade] = useState("");
  const [cidades, setCidades] = useState([]);
  const [rgFrente, setRgFrente] = useState(null);
  const [rgVerso, setRgVerso] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalChavePixAberto, setModalChavePixAberto] = useState(false);
  const [chavesPix, setChavesPix] = useState([]);
  const { valido, htmlFeedback } = validarSenhaSegura(password);
  const [redesSociais, setRedesSociais] = useState(initialStateRedes);

  const handleRedeSocialChange = (rede, link) => {
    setRedesSociais(prevState => ({
      ...prevState,
      [rede]: link,
    }));
  };

  useEffect(() => {
    if (estado) {
      const estadoSelecionado = EstadosECidades.estados.find(
        (e) => e.sigla === estado
      );
      if (estadoSelecionado) {
        setCidades(estadoSelecionado.cidades);
        setCidade(estadoSelecionado.cidades[0]); // define capital (primeira cidade) como padrão
      }
    } else {
      setCidades([]);
      setCidade("");
    }
  }, [estado]);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        if (userData?.email) {
          const response = await api.post(`/auth/user/email`, {
            email: userData.email,
          });

          const user = response.data.data;

          setNome(user.nome || "");
          setEmail(user.email || "");
          setCpfCnpj(user.cpfCnpj || "");
          setTelefone(user.telefone || "");
          setEstado(user.estado || "");
          setCidade(user.cidade || "");
          setRgFrente(user.documentoFrente ? user.documentoFrente : "");
          setRgVerso(user.documentoVerso ? user.documentoVerso : "");
          setChavesPix(user.chavesPix || []);

          if (user.redesSociais && Array.isArray(user.redesSociais)) {
              const redesPreenchidas = { ...initialStateRedes };
              user.redesSociais.forEach(redeSocial => {
                  redesPreenchidas[redeSocial.rede] = redeSocial.link;
              });
              setRedesSociais(redesPreenchidas); 
          }
        }
      } catch (error) {
        exibirLogs && console.error("Erro ao buscar dados do usuário:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userData?.email]);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      if (password) {
        if (!valido) {
          Swal.fire({
            title: "Senha fraca!",
            html: htmlFeedback,
            icon: "error",
          });
          setLoading(false);
          return;
        }
      }

      const deveRedirecionar = userData?.statusAprovacao === "Não Aprovado";
      const redesSociaisParaEnviar = Object.entries(redesSociais)
        .filter(([rede, link]) => link) // Pega apenas redes com link
        .map(([rede, link]) => ({ rede, link }));

      const payload = {
        nome,
        email,
        cpfCnpj,
        telefone,
        estado,
        cidade,
        ...(password && { password }),
        redesSociais: redesSociaisParaEnviar,
        statusAprovacao: deveRedirecionar
          ? "Análise"
          : userData?.statusAprovacao,
      };

      const response = await api.put(
        `/auth/user/update/${userData.uid}`,
        payload
      );
      if (rgFrente || rgVerso) {
        const idUser = response.data?.data._id;
        const formData = new FormData();
        formData.append("frente", rgFrente);
        formData.append("verso", rgVerso);

        try {
          await api.patch(`/auth/upload-docs/${idUser}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          exibirLogs && console.error("Erro ao enviar documentos:", err);
        }
      }

      if (deveRedirecionar) {
        toast.info(
          "Seus dados foram reenviados para análise. Por favor, aguarde."
        );
      } else {
        toast.success("Dados atualizados com sucesso!");
      }

      await fetchLoggedUser();
      if (deveRedirecionar) {
        navigate("/");
      }
    } catch (error) {
      exibirLogs && console.error("Erro ao atualizar dados:", error);
      toast.error("Erro ao atualizar os dados. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");

    if (action) {
      const targetElement = document.querySelector(".docsView");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [location.search]);

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="wrapper">
        <div className="cadastro">
          <div className="logo">
            <img src="https://i.imgur.com/bMhAXFy.png" alt="Logo link do bem" />
          </div>

          <div className="formCadastro">
            <div className="cadastroTitle">Atualizar dados</div>
            <form className="form">
              <div className="formGroup">
                <label htmlFor="nome">Nome completo</label>
                <input
                  id="nome"
                  className="inputDataForm"
                  type="text"
                  placeholder="Digite seu nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  disabled
                />
              </div>

              <div className="formGroup">
                <label htmlFor="email">E-mail</label>
                <input
                  id="email"
                  className="inputDataForm"
                  type="email"
                  placeholder="Digite seu email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled
                />
              </div>

              <div className="formGroup">
                <label htmlFor="password">Senha</label>
                <input
                  id="password"
                  className="inputForm"
                  type="password"
                  placeholder="********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="formGroup">
                <label htmlFor="cpfCnpj">CPF/CNPJ</label>
                <input
                  id="cpfCnpj"
                  className="inputDataForm"
                  type="text"
                  placeholder="Digite aqui"
                  value={cpfCnpj}
                  onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                  disabled
                />
              </div>

              <div className="formGroup">
                <label htmlFor="telefone">Telefone</label>
                <input
                  id="telefone"
                  className="inputForm"
                  type="text"
                  placeholder="(DDD) 99999-9999"
                  value={telefone}
                  onChange={(e) => setTelefone(formatPhone(e.target.value))}
                  required
                />
              </div>

              <div className="formGroup">
                <label htmlFor="estado">Estado</label>
                <select
                  id="estado"
                  className="inputForm"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  required
                >
                  <option value="">Selecione</option>
                  {EstadosECidades.estados.map((estado) => (
                    <option key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </option>
                  ))}
                </select>
              </div>

              {estado && (
                <div className="formGroup">
                  <label htmlFor="cidade">Cidade</label>
                  <select
                    id="cidade"
                    className="inputForm"
                    value={cidade}
                    onChange={(e) => setCidade(e.target.value)}
                    required
                  >
                    {cidades.map((cidade) => (
                      <option key={cidade} value={cidade}>
                        {cidade}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="formGroup">
                <label>Redes Sociais</label>
                {LISTA_REDES_SOCIAIS.map((rede) => (
                  <div key={rede} className="formGroup" style={{ marginTop: '10px', width: '100%' }}>
                    <label htmlFor={`rede-${rede}`}>Link do {rede}</label>
                    <input
                      id={`rede-${rede}`}
                      className="inputForm"
                      type="url"
                      placeholder={`Cole o link do seu perfil aqui`}
                      value={redesSociais[rede] || ''}
                      onChange={(e) => handleRedeSocialChange(rede, e.target.value)}
                      disabled={userData?.redesSociais?.some(rs => rs.rede === rede)}
                      style={{ 
                        backgroundColor: userData?.redesSociais?.some(rs => rs.rede === rede) ? '#f0ececff' : 'transparent' 
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* <div className="formGroup">
                <label htmlFor="dadosPagamento">Minhas chaves pix</label>
                <button
                  color="primary"
                  type="button"
                  onClick={() => setModalChavePixAberto(true)}
                  style={{ width: "100%" }}
                >
                  Editar dados de pagamento
                </button>
              </div>
              */}

              <div className="formGroup">
                <div className="docsView">
                  <strong>Meus documentos:</strong>
                  <div className="upload-group">
                    <UploadButton
                      label="Frente"
                      type="file"
                      onFileChange={(file) => {
                        setRgFrente(file);
                      }}
                      file={rgFrente}
                      required
                      isDisabled={
                        userData?.statusAprovacao === "Aprovado" &&
                        userData?.status === "ativo" &&
                        (userData?.documentoFrente || userData?.documentoVerso)
                      }
                    />
                    <UploadButton
                      label="Verso"
                      onFileChange={(file) => {
                        setRgVerso(file);
                      }}
                      file={rgVerso}
                      required
                      isDisabled={
                        userData?.statusAprovacao === "Aprovado" &&
                        userData?.status === "ativo" &&
                        (userData?.documentoFrente || userData?.documentoVerso)
                      }
                    />
                  </div>

                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {userData?.statusAprovacao === "Aprovado" &&
                      (userData?.documentoFrente ||
                        userData?.documentoVerso) && (
                        <span style={{ textAlign: "center" }}>
                          <RiProhibitedLine color="red" /> Para atualizar seus
                          documentos, entre em contato com o suporte e envie
                          seus documentos atualizados!
                        </span>
                      )}
                  </div>
                </div>
              </div>

              <button type="button" onClick={handleUpdate}>
                Atualizar dados!
              </button>
            </form>
          </div>
        </div>
      </div>

      <ModalChavesPix
        isOpen={modalChavePixAberto}
        onClose={() => setModalChavePixAberto(false)}
        chavesPix={chavesPix}
      />
    </>
  );
}

export default AtualizarDados;
